/**
 * The top-right toolbar's shared **open-state** (ADR-0052):
 * a single "which panel is open" source of truth so the toolbar's popover panels
 * (wardrobe, notifications, settings, and any future sibling) are **mutually
 * exclusive** - opening one closes whichever was open. Before this, each panel owned
 * its open flag independently, so the wardrobe and the notifications dropdown could sit
 * open at once, overlapping in the same corner.
 *
 * This module owns **only** the coordination, not any DOM: each panel keeps managing
 * its own markup/visibility (`wardrobe.ts` / `notifications.ts` / `settings.ts`) and
 * simply **registers a force-close callback** here and **reports its own open-state
 * changes** through {@link ToolbarGroup.setOpen}. So it is pure and unit-tests
 * headlessly with no jsdom, exactly the mutual-exclusion invariants a browser check
 * would eyeball (opening B closes A; toggling the open one closes it; at most one open).
 *
 * The order inside `setOpen(key, true)` matters: it closes the *other* panels first and
 * records the new open key last. A panel's force-close synchronously drives that panel's
 * own `setOpen(key, false)` back through here; doing the bookkeeping in this order means
 * that nested "closed" report clears the *old* key before the outer call installs the
 * new one, so the final state is exactly one open panel. Closing never re-opens anything,
 * so there is no risk of a cross-close loop.
 */

/** The coordinator `main.ts` wires the toolbar panels into; also unit-tested directly. */
export interface ToolbarGroup {
  /**
   * Register a panel under a stable key with a callback that force-closes its UI
   * (idempotent - closing an already-closed panel must be a no-op in the panel).
   */
  register(key: string, close: () => void): void;
  /**
   * A panel reports its open-state changed. `open === true` closes every *other*
   * registered panel and records this one as the sole open panel; `open === false`
   * clears it from the open-state if it was the open one.
   */
  setOpen(key: string, open: boolean): void;
  /** The key of the currently-open panel, or `null` when the toolbar is all-closed. */
  openKey(): string | null;
}

export function createToolbarGroup(): ToolbarGroup {
  // key -> force-close callback for that panel.
  const panels = new Map<string, () => void>();
  // The single source of truth: which panel (if any) is currently open.
  let openKey: string | null = null;

  return {
    register(key, close) {
      panels.set(key, close);
    },
    setOpen(key, open) {
      if (open) {
        // Close every sibling *first* (each close is idempotent, so an already-closed
        // panel is untouched), then install this key as the sole open panel. A sibling's
        // close re-enters here as setOpen(sibling, false), which clears the old key
        // before we set the new one below.
        for (const [k, close] of panels) {
          if (k !== key) close();
        }
        openKey = key;
      } else if (openKey === key) {
        openKey = null;
      }
    },
    openKey: () => openKey,
  };
}
